/**
 * FEMETRY — hero slider web component.
 * Plain custom element (no build step, per Horizon conventions): translateX
 * track, arrows, dots, autoplay with pause on hover/hidden tab, and
 * pointer-based drag/swipe with click suppression after a drag.
 */
class FemetryHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[ref="track"]');
    this.slides = [...this.querySelectorAll('.femetry-hero__slide')];
    this.dots = [...this.querySelectorAll('.femetry-hero__dot')];
    this.index = 0;
    this.timer = null;
    this.dragging = false;
    this.dragMoved = false;

    if (this.slides.length < 2) return;

    this.querySelector('[ref="prev"]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[ref="next"]')?.addEventListener('click', () => this.go(this.index + 1, true));
    this.dots.forEach((dot) =>
      dot.addEventListener('click', () => this.go(Number(dot.dataset.index), true))
    );

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => { if (!this.dragging) this.start(); });
    document.addEventListener('visibilitychange', () =>
      document.hidden ? this.stop() : this.start()
    );

    this.setUpDrag();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  setUpDrag() {
    const track = this.track;
    let startX = 0;
    let deltaX = 0;

    track.style.touchAction = 'pan-y';

    track.addEventListener('pointerdown', (e) => {
      // left button / touch only; let links receive the press
      if (e.button > 0) return;
      this.dragging = true;
      this.dragMoved = false;
      startX = e.clientX;
      deltaX = 0;
      this.stop();
      track.style.transition = 'none';
      track.setPointerCapture(e.pointerId);
    });

    track.addEventListener('pointermove', (e) => {
      if (!this.dragging) return;
      deltaX = e.clientX - startX;
      if (Math.abs(deltaX) > 10) this.dragMoved = true;
      // resist at the ends
      const atEdge =
        (this.index === 0 && deltaX > 0) ||
        (this.index === this.slides.length - 1 && deltaX < 0);
      const drag = atEdge ? deltaX / 3 : deltaX;
      track.style.transform = `translateX(calc(-${this.index * 100}% + ${drag}px))`;
    });

    const release = () => {
      if (!this.dragging) return;
      this.dragging = false;
      track.style.transition = '';
      const threshold = Math.min(80, this.offsetWidth * 0.15);
      if (deltaX <= -threshold) this.go(this.index + 1);
      else if (deltaX >= threshold) this.go(this.index - 1);
      else this.go(this.index);
      this.start();
    };
    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);

    // a drag must not fire the slide's link
    track.addEventListener('click', (e) => {
      if (this.dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        this.dragMoved = false;
      }
    }, true);

    // native image/text drag interferes with pointer tracking
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }

  go(to, user = false) {
    const count = this.slides.length;
    this.index = Math.max(0, Math.min(count - 1, ((to % count) + count) % count));
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === this.index));
    if (user) {
      this.stop();
      this.start();
    }
  }

  start() {
    if (this.dataset.autoplay !== 'true' || this.timer) return;
    const interval = Number(this.dataset.interval) || 5000;
    this.timer = setInterval(() => {
      // autoplay wraps; manual go() clamps at the ends
      const next = (this.index + 1) % this.slides.length;
      this.go(next);
    }, interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('femetry-hero')) {
  customElements.define('femetry-hero', FemetryHero);
}
